import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { 
  Database, 
  Trash2, 
  RefreshCw, 
  BarChart3,
  HardDrive,
  Activity,
  AlertTriangle,
  CheckCircle,
  Zap
} from 'lucide-react'
import { cacheApi, CacheStatsResponse } from '@/lib/api'
import { toast } from 'sonner'

interface CacheTabProps {
  serverId: string
  serverName: string
}

export function CacheTab({ serverId, serverName }: CacheTabProps) {
  const [cacheStats, setCacheStats] = useState<CacheStatsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [clearingCache, setClearingCache] = useState(false)

  // Fetch cache stats for the server
  const fetchCacheStats = async () => {
    setRefreshing(true)
    try {
      const stats = await cacheApi.getStats(serverId)
      setCacheStats(stats)
    } catch (error) {
      console.error(`Failed to fetch cache stats for server ${serverId}:`, error)
      // Set default stats for servers without cache
      setCacheStats({
        server_id: serverId,
        cache_enabled: false,
        stats: {
          cache_enabled: false,
          hits: 0,
          misses: 0,
          hit_rate: '0.00%',
          size: 0,
          max_size: 0,
          memory_usage: '0.00 KB',
          evictions: 0,
          last_cleanup: null,
          average_latency: '0s',
          default_ttl: '0s',
          max_ttl: '0s',
        },
        server_info: {
          name: serverName,
          transport: 'unknown',
          status: 'unknown',
          created_at: new Date().toISOString(),
        }
      })
    } finally {
      setRefreshing(false)
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCacheStats()
  }, [serverId])

  const handleClearCache = async () => {
    setClearingCache(true)
    try {
      await cacheApi.clear(serverId)
      toast.success('Cache cleared successfully')
      fetchCacheStats() // Refresh stats
    } catch (error) {
      console.error('Failed to clear cache:', error)
      toast.error('Failed to clear cache')
    } finally {
      setClearingCache(false)
    }
  }

  const handleInvalidateCache = async (pattern: string = '', all: boolean = false) => {
    try {
      await cacheApi.invalidate(serverId, {
        pattern,
        all
      })
      toast.success(all ? 'All cache entries invalidated' : `Cache entries matching "${pattern}" invalidated`)
      fetchCacheStats() // Refresh stats
    } catch (error) {
      console.error('Failed to invalidate cache:', error)
      toast.error('Failed to invalidate cache entries')
    }
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Database className="h-5 w-5" />
            <span>Cache Settings</span>
          </CardTitle>
          <CardDescription>
            Loading cache configuration and statistics...
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-500 mr-2" />
            <span className="text-muted-foreground">Loading cache data...</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!cacheStats) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Database className="h-5 w-5" />
            <span>Cache Settings</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Failed to load cache information for this server.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Cache Management</h2>
          <p className="text-muted-foreground">
            View and manage cache configuration for {serverName}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Badge variant={cacheStats.cache_enabled ? "default" : "secondary"}>
            {cacheStats.cache_enabled ? "Cache Enabled" : "Cache Disabled"}
          </Badge>
          <Button variant="outline" onClick={fetchCacheStats} disabled={refreshing}>
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {cacheStats.cache_enabled ? (
        <div className="space-y-6">
          {/* Overview Statistics */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Cache Hit Rate</CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{cacheStats.stats.hit_rate}</div>
                <div className="text-xs text-muted-foreground">
                  {cacheStats.stats.hits.toLocaleString()} hits, {cacheStats.stats.misses.toLocaleString()} misses
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Cache Entries</CardTitle>
                <Database className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{cacheStats.stats.size.toLocaleString()}</div>
                <div className="text-xs text-muted-foreground">
                  of {cacheStats.stats.max_size.toLocaleString()} max
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Memory Usage</CardTitle>
                <HardDrive className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{cacheStats.stats.memory_usage}</div>
                <div className="text-xs text-muted-foreground">
                  Memory consumed
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Latency</CardTitle>
                <Zap className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{cacheStats.stats.average_latency}</div>
                <div className="text-xs text-muted-foreground">
                  Response time
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Visual Statistics */}
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Cache Performance</CardTitle>
                <CardDescription>Hit rate and usage metrics</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Hit Rate</Label>
                    <span className="text-sm font-semibold text-green-600">{cacheStats.stats.hit_rate}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-green-600 h-3 rounded-full" 
                      style={{ width: `${parseFloat(cacheStats.stats.hit_rate.replace('%', ''))}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Cache Usage</Label>
                    <span className="text-sm font-semibold">
                      {cacheStats.stats.size}/{cacheStats.stats.max_size}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-blue-600 h-3 rounded-full" 
                      style={{ width: `${(cacheStats.stats.size / Math.max(cacheStats.stats.max_size, 1)) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Cache Configuration</CardTitle>
                <CardDescription>Current cache settings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Default TTL</Label>
                    <div className="p-2 bg-gray-50 rounded text-sm">{cacheStats.stats.default_ttl}</div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Maximum TTL</Label>
                    <div className="p-2 bg-gray-50 rounded text-sm">{cacheStats.stats.max_ttl}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Statistics */}
          <Card>
            <CardHeader>
              <CardTitle>Detailed Statistics</CardTitle>
              <CardDescription>Comprehensive cache metrics and performance data</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-sm font-medium text-green-800">Cache Hits</div>
                  <div className="text-2xl font-bold text-green-600">{cacheStats.stats.hits.toLocaleString()}</div>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <div className="text-sm font-medium text-red-800">Cache Misses</div>
                  <div className="text-2xl font-bold text-red-600">{cacheStats.stats.misses.toLocaleString()}</div>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <div className="text-sm font-medium text-orange-800">Evictions</div>
                  <div className="text-2xl font-bold text-orange-600">{cacheStats.stats.evictions.toLocaleString()}</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-sm font-medium text-blue-800">Entries</div>
                  <div className="text-2xl font-bold text-blue-600">{cacheStats.stats.size.toLocaleString()}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cache Management Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Cache Management</CardTitle>
              <CardDescription>Control and manage cache operations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-1">
                  <div className="font-medium">Clear Cache</div>
                  <div className="text-sm text-muted-foreground">
                    Remove all cached entries for this server
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Last cleanup: {cacheStats.stats.last_cleanup ? new Date(cacheStats.stats.last_cleanup).toLocaleString() : 'Never'}
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={handleClearCache}
                  disabled={clearingCache}
                >
                  {clearingCache ? (
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="mr-2 h-4 w-4" />
                  )}
                  {clearingCache ? 'Clearing...' : 'Clear Cache'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Database className="h-5 w-5" />
              <span>Cache Not Enabled</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Cache is not enabled for this server. To enable caching, you need to configure it when creating or updating the server configuration.
              </AlertDescription>
            </Alert>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">
                Caching can significantly improve performance by storing API responses in memory and reducing redundant requests to external services.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
} 